import { useState } from 'react';
import { loadDisplayFilters, saveDisplayFilters } from './display-state.js';

/**
 * A screen's filters, REMEMBERED (0.1.522, maintainer 1 Oct 2026: "Applying filters should remain
 * through tab switches and app reloads, the same for group and sort. This should be for everywhere
 * in the app that has these display options"). The one shape every filtered sheet uses — Items,
 * Boxes, Outfits, Reservations, Check-outs, Projects, Retour — hoisted at the third copy.
 *
 * `sanitize` turns the stored, unvalidated value into the screen's own filter shape, or null when
 * it cannot (corrupt, or from an older build): then `fallback` applies — the screen's RESTING
 * filter, `{}` everywhere except Check-outs. A cleared filter is saved as cleared, so the resting
 * filter returns only on a device that never stored one (intent kept apart from the default).
 *
 * `persist: false` keeps the old session-only behaviour for the instances that must not share the
 * catalogue's memory — a box's or a reservation's own garment list (items.tsx, scoped).
 */
export function useStoredFilters<T>(
  screen: string,
  sanitize: (raw: unknown) => T | null,
  fallback: T | (() => T),
  persist = true,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [filters, setFilters] = useState<T>(() => {
    const restored = persist ? sanitize(loadDisplayFilters(screen)) : null;
    if (restored !== null) return restored;
    return typeof fallback === 'function' ? (fallback as () => T)() : fallback;
  });
  const set = (next: T | ((prev: T) => T)): void => {
    setFilters((prev) => {
      const value = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      if (persist) saveDisplayFilters(screen, value);
      return value;
    });
  };
  return [filters, set];
}
